<template>
  <b-card class="billing-card h-100">
    <!-- Billing Address Section -->
    <div class="section-header">
      <feather-icon
        icon="MapPinIcon"
        size="20"
        class="section-icon"
      />
      <h5 class="section-title">
        Rechnungsanschrift
      </h5>
    </div>
    
    <div class="address-section">
      <div class="info-row">
        <div class="info-label">
          Strasse
        </div>
        <div class="info-value">
          {{ (customerEntry.billingStreet || customerEntry.street || '') }} 
          {{ (customerEntry.billingStreetNumber || customerEntry.streetNumber || '') }}
        </div>
      </div>
      
      <div class="info-row">
        <div class="info-label">
          PLZ | Stadt
        </div>
        <div class="info-value">
          {{ (customerEntry.billingZipCode || customerEntry.zipCode || '') }} 
          {{ (customerEntry.billingCity || customerEntry.city || '') }}
        </div>
      </div>
      
      <div
        v-if="customerEntry.billingCountry || customerEntry.country"
        class="info-row"
      >
        <div class="info-label">
          Land
        </div>
        <div class="info-value">
          {{ customerEntry.billingCountry || customerEntry.country }}
        </div>
      </div>
    </div>

    <!-- Payment Information Section -->
    <div class="section-header payment-header">
      <feather-icon
        icon="CreditCardIcon"
        size="20"
        class="section-icon"
      />
      <h5 class="section-title">
        Bezahlinformationen
      </h5>
    </div>
    
    <div class="payment-section">
      <div class="info-row">
        <div class="info-label">
          Bezahlart
        </div>
        <div class="info-value">
          <span class="payment-badge sepa-badge">
            <feather-icon
              icon="CheckCircleIcon"
              size="14"
            />
            SEPA Lastschrift
          </span>
        </div>
      </div>
      
      <div class="info-row">
        <div class="info-label">
          Bank
        </div>
        <div class="info-value">
          {{ customerEntry.bankName || '-' }}
        </div>
      </div>
      
      <div class="info-row">
        <div class="info-label">
          IBAN
        </div>
        <div class="info-value">
          <span
            v-if="customerEntry.bankIBAN"
            class="iban-code"
          >
            {{ formatIBAN(customerEntry.bankIBAN) }}
          </span>
          <span
            v-else
            class="empty-value"
          >-</span>
        </div>
      </div>
      
      <div class="info-row">
        <div class="info-label">
          BIC
        </div>
        <div class="info-value">
          <span v-if="customerEntry.bankBIC">{{ customerEntry.bankBIC }}</span>
          <span
            v-else
            class="empty-value"
          >-</span>
        </div>
      </div>
      
      <div class="info-row">
        <div class="info-label">
          Kreditkarte
        </div>
        <div class="info-value">
          <span class="payment-badge disabled-badge">
            <feather-icon
              icon="XIcon"
              size="14"
            />
            Nicht verfügbar
          </span>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BCard, BRow, BCol, BButton, BBadge } from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BButton,
    BRow,
    BCol,
    BBadge,
  },
  directives: {},
  props: {
    customerEntry: {
      type: Object,
      required: false,
    },
  },
  methods: {
    formatIBAN(iban) {
      if (!iban) return ''
      // Add spaces every 4 characters for better readability
      return iban.replace(/(.{4})/g, '$1 ').trim()
    },
    // warning
    deleteWarning() {
      this.$swal({
        title: 'Bist du sicher?',
        text: 'Alle Informationen zu dem Kunden (Kundendetails, Buchungen, Einnahmen, Aktivitäten, etc.) werden unwiederruflich gelöscht.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, löschen!',
        cancelButtonText: 'Abbrechen',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-primary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$swal({
            icon: 'success',
            title: 'Deleted!',
            text: 'Your file has been deleted.',
            customClass: {
              confirmButton: 'btn btn-success',
            },
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-sweetalert.scss';

@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.billing-card {
  border: 1px solid #e9ecef;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  
  .card-body {
    padding: 2rem;
  }
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  
  &.payment-header {
    margin-top: 2rem;
  }
  
  .section-icon {
    color: #007bff;
    margin-right: 0.75rem;
  }
  
  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }
}

.address-section, .payment-section {
  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0.875rem 0;
    border-bottom: 1px solid #f1f3f4;
    
    &:last-child {
      border-bottom: none;
    }
    
    .info-label {
      font-size: 0.875rem;
      color: #8a92b2;
      font-weight: 500;
      min-width: 80px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .info-value {
      font-size: 1rem;
      color: #2c3e50;
      font-weight: 500;
      text-align: right;
      flex: 1;
      
      .empty-value {
        color: #adb5bd;
        font-style: italic;
      }
      
      .iban-code {
        font-family: 'Monaco', 'Consolas', 'Liberation Mono', monospace;
        font-size: 0.9rem;
        background: #f8f9fa;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        letter-spacing: 1px;
        color: #495057;
      }
      
      .payment-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.375rem 0.75rem;
        border-radius: 20px;
        font-size: 0.875rem;
        font-weight: 500;
        
        .feather {
          margin-right: 0.375rem;
        }
        
        &.sepa-badge {
          background: #d4edda;
          color: #155724;
          
          .feather {
            color: #28a745;
          }
        }
        
        &.disabled-badge {
          background: #f8d7da;
          color: #721c24;
          
          .feather {
            color: #dc3545;
          }
        }
      }
    }
  }
}
</style>
